import { useMutation } from 'react-query';

import { PAYMENTS_BACKEND_URL, RECAPTCHA_NEEDED } from '@/config';
import axios from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';

export type RequestMobileTokenProps = {
  hash: string;
  phone: string;
  recaptcha: string;
};

export type RequestMobileTokenResponseProps = {
  id: null;
  groupId: null;
  phone: string;
  lang: string;
  tries: number;
  deliveries: number;
  validAt: string;
  usedAt: null;
  createdAt: null;
};

export const requestMobileToken = ({
  hash,
  phone,
  recaptcha,
}: RequestMobileTokenProps): Promise<RequestMobileTokenResponseProps> => {
  const endPoint = PAYMENTS_BACKEND_URL + 'mobile-token/send';
  let data;
  if (RECAPTCHA_NEEDED) {
    data = { hash, phone, 'cf-turnstile-response': recaptcha };
  } else {
    data = { hash, phone };
  }

  return axios
    .post(endPoint, data, {
      validateStatus: (status) => status < 500,
    })
    .then((response: any) => {
      if (typeof response === 'string') {
        throw Error(`Critical error fetching data in ${endPoint}`);
      }

      return response;
    })
    .catch((error) => {
      throw Error(`Critical error fetching data in ${endPoint}: ${error}`);
    });
};

type UseRequestMobileToken = {
  config?: MutationConfig<typeof requestMobileToken>;
};

export const useRequestMobileToken = ({ config }: UseRequestMobileToken = {}) => {
  return useMutation({
    onMutate: async (creatingNotification: any) => {
      await queryClient.cancelQueries(['mobile-token', creatingNotification]);

      const previousDetails = queryClient.getQueryData<RequestMobileTokenProps>([
        'mobile-token',
        creatingNotification,
      ]);

      queryClient.setQueryData(['mobile-token', creatingNotification], {
        ...previousDetails,
        ...creatingNotification.data,
      });

      return { previousDetails: previousDetails };
    },

    onError: (_, __, context: any) => {
      if (context?.previousDetails) {
        queryClient.setQueryData(
          ['mobile-token', context.previousDetails],
          context.previousDetails
        );
      }
    },

    onSuccess: (data) => {
      queryClient.refetchQueries(['mobile-token', data]);
    },
    ...config,

    mutationFn: requestMobileToken,
  });
};
